import React, { useState } from "react";
import { Box, Modal, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as NewDesignIcon } from "../../../../assets/svg/NewDesignIcon.svg";
import { renderCurrencyIcon } from "../../../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../../../utils/CommonFunction";

const DEFAULT_IMAGE =
  "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b";

const styles = {
  container: {
    fontFamily: "Inter Tight",
    padding: "16px",
  },
  text: {
    color: "#949DAA",
    fontSize: "13px",
    fontWeight: 400,
    lineHeight: "20px",
    margin: 0,
  },
  sub_heading: {
    color: "#0D0D0E",
    fontSize: "14px",
    fontWeight: 500,
    margin: 0,
  },
  small_sub_heading: {
    color: "#0D0D0E",
    fontSize: "14px",
    fontWeight: 500,
  },
  columnFlex: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  rowFlex: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
  },
  idLabel: {
    color: "#949DAA",
    fontSize: "12px",
    margin: 0,
  },
  projectHeading: {
    color: "#0D0D0E",
    fontSize: "16px",
    fontWeight: "600",
    margin: 0,
  },
  price: {
    color: "#0D0D0E",
    fontSize: "16px",
    fontWeight: "500",
    margin: 0,
  },
};

const ProjectSidebar = ({ bidDetails, isCollapsed }) => {
  // Same hero-image resolution as the Review tab — only honour the
  // untagged contractor upload, then fall through to default_image[0],
  // then to a static placeholder.
  const projectImages = Array.isArray(bidDetails?.project_image)
    ? bidDetails.project_image
    : [];
  const primaryProjectImage =
    projectImages.find((img) => img && img.tags === null) || null;
  const projectImage =
    primaryProjectImage?.image ||
    bidDetails?.default_image?.[0]?.image ||
    DEFAULT_IMAGE;

  const [lightboxOpen, setLightboxOpen] = useState(false);

  return (
    <Box
      sx={{
        width: isCollapsed ? "0px" : "35px",
        minWidth: isCollapsed ? "0px" : "350px",
        // backgroundColor: "#F9FAFB",
        // borderLeft: isCollapsed ? "none" : "1px solid #E5E7EB",
        height: "100%",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        padding: "0 1vw"
      }}
    >
      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          opacity: isCollapsed ? 0 : 1,
          transition: "opacity 0.2s ease",
          // Hide scrollbar but keep scroll functionality
          "&::-webkit-scrollbar": {
            width: "0px",
            background: "transparent",
          },
          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none", // IE and Edge
        }}
      >
        <div style={styles.container}>

          {/* Project Image and Name */}
          <Box
            sx={{
              display: "flex",
              gap: "12px",
              marginBottom: "20px",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={projectImage}
              alt="Project"
              onError={(e) => (e.currentTarget.src = DEFAULT_IMAGE)}
              onClick={() => setLightboxOpen(true)}
              sx={{
                width: 120,
                height: 120,
                borderRadius: "12px",
                flexShrink: 0,
                border: "1px solid #E5E7EB",
                cursor: "zoom-in",
                objectFit: "cover",
              }}
            />
            <div style={{ ...styles.columnFlex, gap: "4px" }}>
              <div style={{ ...styles.rowFlex, gap: "8px" }}>
                <p style={{ ...styles.idLabel, ...styles.rowFlex, gap: "4px", margin: 0 }}>
                  <NewDesignIcon style={{ width: 14, height: 14 }} /> {bidDetails?.project_type}
                </p>
              </div>
              <h2 style={styles.projectHeading}>{bidDetails?.name}</h2>
            </div>
          </Box>

          {/* Cost and Customer */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ ...styles.columnFlex, flex: 1, gap: "4px" }}>
              <p style={styles.idLabel}>Cost</p>
              <div
                style={{
                  ...styles.small_sub_heading,
                  ...styles.rowFlex,
                  gap: "4px",
                }}
              >
                {renderCurrencyIcon("black-small")}
                {bidDetails?.scope_boqs?.total_amount  ? formattedAmountWithoutAed(bidDetails?.scope_boqs?.total_amount ) : "-"}
              </div>
            </div>

            {/* <div style={{ ...styles.columnFlex, flex: 1, gap: "4px" }}>
              <p style={styles.idLabel}>Customer</p>
              <Box display="flex" alignItems="center">
                {bidDetails?.user_data && (
                  <Box
                    component="img"
                    src={
                      bidDetails?.user_data?.profile_url ||
                      "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
                    }
                    alt="Customer"
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      boxShadow: "0px 4px 2.67px 0px #00000026",
                      marginRight: 1,
                    }}
                  />
                )}
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  {bidDetails?.user_data?.username}
                </span>
              </Box>
            </div> */}
          </Box>

          {/* Unit Type, Unit, Unit Size */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div style={{ ...styles.rowFlex, justifyContent: "space-between", gap: "4px" }}>
              <h3 style={styles.text}>Unit Type</h3>
              <div style={styles.sub_heading}>{bidDetails?.unit_type || "-"}</div>
            </div>
            <div style={{ ...styles.rowFlex, justifyContent: "space-between", gap: "4px" }}>
              <h3 style={styles.text}>Unit</h3>
              <div style={styles.sub_heading}>{bidDetails?.unit || "-"}</div>
            </div>
            <div style={{ ...styles.rowFlex, justifyContent: "space-between", gap: "4px" }}>
              <h3 style={styles.text}>Unit Size</h3>
              <div style={styles.sub_heading}>{bidDetails?.unit_size || "-"}</div>
            </div>
          </Box>

          <div style={{ ...styles.columnFlex, marginBottom: "16px" }}>
            <h3 style={styles.text}>Location</h3>
            <div style={styles.sub_heading}>{bidDetails?.location || "-"}</div>
          </div>

        </div>
      </Box>

      {/* Lightbox — opens the hero image at native size on a dimmed
          backdrop. Click anywhere outside the image (or the × button)
          to close. */}
      <Modal
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        aria-labelledby="project-image-lightbox"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Box
          onClick={() => setLightboxOpen(false)}
          sx={{
            position: "relative",
            outline: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            maxWidth: "92vw",
            maxHeight: "92vh",
          }}
        >
          <Box
            component="img"
            src={projectImage}
            alt="Project"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => (e.currentTarget.src = DEFAULT_IMAGE)}
            sx={{
              maxWidth: "92vw",
              maxHeight: "92vh",
              borderRadius: "12px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
              objectFit: "contain",
              backgroundColor: "#000",
            }}
          />
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "rgba(0,0,0,0.55)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Modal>
    </Box>
  );
};

export default ProjectSidebar;
